import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "../Contexts/AuthenticatedUserContext";
import { UserxRole } from "../DTO/Userx";
import { cn } from "../lib/utils";
import { ChevronDown, Menu, X } from "lucide-react";
import logo from "../assets/logo-gentian.png";

const CONTACT_MAIL = "mailto:repetschnig.manuel@gmail.com";

const NavbarComponent: React.FC = () => {
    const { currentUser } = useUser();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const baseItems = [
        { label: "Projects", url: "/projects" },
        { label: "CV", url: "/cv" },
        { label: "Blog", url: "/blog" },
    ];
    const adminItems = [
        { label: "Manage users", url: "/manage-users" },
        { label: "Manage projects", url: "/manage-projects" },
        { label: "Manage technologies", url: "/manage-technologies" },
        { label: "Manage blog posts", url: "/manage-blogs" },
        { label: "Log out", url: "/logout" },
    ];

    const isAdmin = currentUser?.roles.includes(UserxRole.ADMIN);
    const isActive = (url: string) =>
        location.pathname === url || location.pathname.startsWith(url + "/");

    const itemClass = (active: boolean) =>
        cn(
            "font-label text-base font-medium border-b-2 pb-0.5 transition-colors",
            active
                ? "text-ink border-gentian"
                : "text-muted border-transparent hover:text-ink"
        );

    return (
        <nav className="sticky top-0 z-40 bg-surface border-b border-rule">
            <div className="page-container flex h-16 items-center justify-between">
                <Link to="/" aria-label="Manuel Repetschnig, home" className="flex items-center gap-3">
                    <img src={logo} alt="" className="h-7 w-auto" />
                    <span className="font-display text-lg font-semibold text-ink">
                        Manuel Repetschnig
                    </span>
                </Link>

                {/* Desktop */}
                <div className="hidden md:flex items-center gap-7">
                    {baseItems.map(item => (
                        <Link
                            key={item.url}
                            to={item.url}
                            aria-current={isActive(item.url) ? "page" : undefined}
                            className={itemClass(isActive(item.url))}
                        >
                            {item.label}
                        </Link>
                    ))}
                    <a href={CONTACT_MAIL} className={itemClass(false)}>
                        Contact
                    </a>

                    {isAdmin && (
                        <div className="relative group">
                            <button
                                className="inline-flex items-center gap-1 font-label text-base font-medium text-muted hover:text-ink"
                                aria-haspopup="true"
                            >
                                Admin <ChevronDown size={16} aria-hidden />
                            </button>
                            <div className="absolute right-0 mt-2 w-52 bg-surface border border-rule rounded shadow-md py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity">
                                {adminItems.map(item => (
                                    <Link
                                        key={item.url}
                                        to={item.url}
                                        className="block px-4 py-2 font-label text-sm text-ink hover:bg-paper"
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile toggle */}
                <button
                    className="md:hidden p-2 -mr-2 rounded-sm text-ink hover:bg-paper"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={menuOpen ? "Close menu" : "Open menu"}
                    aria-expanded={menuOpen}
                >
                    {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Mobile menu */}
            {menuOpen && (
                <div className="md:hidden border-t border-rule bg-surface">
                    <div className="page-container py-4 flex flex-col gap-3">
                        {baseItems.map(item => (
                            <Link
                                key={item.url}
                                to={item.url}
                                aria-current={isActive(item.url) ? "page" : undefined}
                                className={cn(itemClass(isActive(item.url)), "self-start")}
                                onClick={() => setMenuOpen(false)}
                            >
                                {item.label}
                            </Link>
                        ))}
                        <a href={CONTACT_MAIL} className={cn(itemClass(false), "self-start")}>
                            Contact
                        </a>
                        {isAdmin && (
                            <div className="flex flex-col gap-2 pt-3 mt-1 border-t border-rule">
                                {adminItems.map(item => (
                                    <Link
                                        key={item.url}
                                        to={item.url}
                                        className="font-label text-sm text-muted hover:text-ink"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default NavbarComponent;
